<template>
  <form @submit="props.handleSubmit" class="space-y-6">
    <!-- <h1 class="text-lg font-semibold">Expedition Info</h1> -->
    <FormField v-slot="{ componentField }" name="name">
      <FormItem>
        <FormLabel>Expedition Name</FormLabel>
        <FormControl>
          <Input
            type="text"
            placeholder="e.g., Expedition10"
            v-bind="componentField"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField v-slot="{ componentField }" name="details">
      <FormItem>
        <FormLabel>Expedition Details</FormLabel>
        <FormControl>
          <Textarea
            placeholder="Brief description of the expedition"
            v-bind="componentField"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField v-slot="{ field }" name="date">
       <FormItem class="flex flex-col">
         <FormLabel>Start date</FormLabel>
         <Popover>
           <PopoverTrigger as-child>
             <FormControl>
               <Button
                 variant="outline"
                 :class="
                   cn(
                     'w-[240px] justify-start text-left font-normal',
                     !field.value && 'text-muted-foreground'
                   )
                 "
               >
                 <CalendarIcon class="mr-2 h-4 w-4" />
                 {{ field.value ? format(field.value, 'PPP') : 'Pick a date' }}
               </Button>
             </FormControl>
           </PopoverTrigger>
           <PopoverContent class="w-auto p-0">
             <Calendar
               :model-value="field.value ? new CalendarDate(field.value.getFullYear(), field.value.getMonth() + 1, field.value.getDate()) : undefined"
               @update:model-value="(value: DateValue | undefined) => {
                 if (value instanceof CalendarDate) {
                   // Use the passed setFieldValue prop
                   props.setFieldValue('date', new Date(value.year, value.month - 1, value.day))
                 } else {
                   props.setFieldValue('date', undefined)
                 }
               }"
               initial-focus
             />
           </PopoverContent>
         </Popover>
         <FormMessage />
       </FormItem>
     </FormField>

    <FormField v-slot="{ componentField }" name="path_id">
      <FormItem>
        <FormLabel>Path <span class="text-red-500">*</span></FormLabel>
        <Select v-bind="componentField">
          <FormControl>
            <SelectTrigger>
              <SelectValue placeholder="Select a path" />
            </SelectTrigger>
          </FormControl>
          <SelectContent>
            <SelectGroup>
              <SelectItem
                v-for="path in pathsStore.paths"
                :key="path.id"
                :value="path.id.toString()"
              >
                {{ path.name }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
        <FormDescription>You must select a path for the expedition.</FormDescription>
        <FormMessage />
      </FormItem>

      <FormField name="new_path">
        <FormItem class="flex flex-row items-start space-x-3 space-y-0 rounded-md">
          <FormControl>
            <Checkbox
              v-model="newPathChecked"
              @update:model-value="updateNewPath"
              class="cursor-pointer"
            />
          </FormControl>
          <div class="space-y-1 leading-none">
            <FormLabel class="cursor-pointer">Create New Path</FormLabel>
            <FormDescription>
              Check this if you want to create a new path based on the selected path.
            </FormDescription>
          </div>
        </FormItem>
      </FormField>
    </FormField>

    
  </form>
</template>

<script setup lang="ts">
import { defineProps, type PropType, ref, onMounted } from 'vue'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { CalendarDate, type DateValue } from '@internationalized/date'
import { CalendarIcon } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { usePathsStore } from '@/stores/paths'

const pathsStore = usePathsStore()

// Define local type if not exported from store
interface ExpeditionType {
  id: number;
  name: string;
  // Add other relevant fields if known
}

// Define Path type for clarity (adjust if needed based on actual store structure)
interface Path {
  id: number;
  name: string;
  created_datetime: string;
  expedition_type: number;
  pathmaker: number;
  // Add other relevant fields if known
}

// Define Props
const props = defineProps({
  handleSubmit: {
    type: Function as PropType<(e?: Event | undefined) => void>,
    required: true
  },
  setFieldValue: {
    type: Function as PropType<(field: string, value: any) => void>,
    required: true
  },
  expeditionTypes: {
    type: Array as PropType<ExpeditionType[]>,
    required: true,
    default: () => []
  }
})

const newPathChecked = ref(false)

const updateNewPath = () => {
  console.log('Checkbox changed:', newPathChecked.value)
  props.setFieldValue('new_path', newPathChecked.value)
}

onMounted(async () => {
  await pathsStore.fetchPaths()
})

</script> 